import { PropsWithChildren, useCallback } from 'react';

import { InternalLinkInterceptor } from '@/components/TextEditor/plugins/InternalLinkPlugin/context/internalLinkInterceptor.context';
import { InternalLinkInterceptorProvider } from '@/components/TextEditor/plugins/InternalLinkPlugin/context/InternalLinkInterceptor.provider';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { useChatSideContentContext } from './context/chatSideContent.context';

/**
 * Routes document-summary internal links (luminary://record/DS_… targets) in
 * chat messages into the side content pane instead of navigating. Mount around
 * a chat surface that has a ChatSideContentProvider; where this interceptor
 * isn't mounted, document-summary links degrade to plain text.
 */
export function ChatSideContentLinkInterceptor({
  children,
}: PropsWithChildren) {
  const { openSideContent } = useChatSideContentContext();

  const interceptor = useCallback<InternalLinkInterceptor>(
    (target) => {
      if (
        target.kind !== 'record' ||
        getPulidKind(target.recordId) !== PulidKind.DocumentSummary
      ) {
        return null;
      }
      const documentSummaryId = target.recordId;
      return () =>
        openSideContent({ type: 'documentSummary', documentSummaryId });
    },
    [openSideContent]
  );

  return (
    <InternalLinkInterceptorProvider interceptor={interceptor}>
      {children}
    </InternalLinkInterceptorProvider>
  );
}
